import React, { useState, useEffect } from 'react';
import { Lock } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { message } from 'antd';
import CalendarGrid, { CalendarSkeleton } from 'pages/Academy/components/CalendarGrid';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  phases,
  evalItems,
  elitePlusStages,
  kpiBigCards,
  kpiPerformanceRows,
  kpiConsistencyRows,
  kpiRiskRows,
  kpiDisciplineRows,
  phaseTimeline,
  weeklyR,
  activities,
  monthlyKpi,
  type DayStatus,
} from 'mock/13w.data';
import { useMonthChecklist } from 'hooks/useThirteenWeeks';
import DayDetail from './DayDetail';
import type { StepKey } from 'mock/journalDay.data';
import ScreenHandbook from './Handbook';
import './styles.scss';
import { t } from 'i18next';

type TabKey = 'overview' | 'dashboard' | 'roadmap' | 'kpi' | 'checklist' | 'eval' | 'eliteplus' | 'handbook';

const VALID_TABS: TabKey[] = ['overview', 'dashboard', 'roadmap', 'kpi', 'checklist', 'eval', 'eliteplus', 'handbook'];

function isValidTab(t: string | null): t is TabKey {
  return VALID_TABS.includes(t as TabKey);
}

// Overview
const OVERVIEW_FEATURES = [
  { icon: '◎', title: "13W_Overview.section_1.title", sub: "13W_Overview.section_1.sub", tab: 'roadmap' },
  { icon: '≡', title: "13W_Overview.section_2.title", sub: "13W_Overview.section_2.sub", tab: 'kpi' },
  { icon: '◈', title: "13W_Overview.section_3.title", sub: "13W_Overview.section_3.sub", tab: 'eval' },
];

function ScreenOverview() {
  return (
    <div className="tw-landing">
      <h1 className="tw-landing__h1" dangerouslySetInnerHTML={{ __html: t("13W_Overview.Title", { interpolation: { escapeValue: false } }) }}></h1>
      <p className="tw-landing__sub" dangerouslySetInnerHTML={{ __html: t("13W_Overview.Subtitle", { interpolation: { escapeValue: false } }) }}></p>
      <div className="tw-landing__cta">
        <a href="/academy?tab=dashboard" className="tw-landing__btn tw-landing__btn--primary">{t("13W_Overview.dashboard_button")} →</a>
        <a href="/academy?tab=roadmap" className="tw-landing__btn tw-landing__btn--outline">{t("13W_Overview.roadmap_button")}</a>
      </div>
      <div className="tw-landing__features">
        {OVERVIEW_FEATURES.map((f) => (
          <a key={f.title} href={`/academy?tab=${f.tab}`} className="tw-landing__feature tw-landing__feature--link">
            <div className="tw-landing__feature-icon">{f.icon}</div>
            <div className="tw-landing__feature-title" dangerouslySetInnerHTML={{ __html: t(f.title, { interpolation: { escapeValue: false } }) }}></div>
            <div className="tw-landing__feature-sub" dangerouslySetInnerHTML={{ __html: t(f.sub, { interpolation: { escapeValue: false } }) }}></div>
          </a>
        ))}
      </div>
    </div>
  );
}

// Dashboard
function ScreenDashboard() {
  const maxR = Math.max(...weeklyR.map((w) => Math.abs(w.r)));

  return (
    <div className="tw-db">
      <div className="tw-db__hero">
        <div className="tw-db__phase-badge">
          <span className="tw-db__phase-dot" />
          ĐANG TIẾN HÀNH
        </div>
        <h2 className="tw-db__hero-title">
          Giai đoạn 2: <span>Ổn định</span>
        </h2>
        <p className="tw-db__hero-sub">
          Tháng 3–4 · Mục tiêu: Có lợi nhuận nhỏ đều đặn, bắt đầu có hệ thống rõ ràng với expectancy dương ổn định.
        </p>
        <div className="tw-db__prog-wrap">
          <div className="tw-db__prog-label">
            <span>{t('13W_Dashboard.roadmap_overview')}</span>
            <strong>T3 / 12</strong>
          </div>
          <div className="tw-db__timeline">
            {phaseTimeline.map((ph) => (
              <div key={ph.id} className={`tw-db__tph tw-db__tph--${ph.status}`}>
                <div className="tw-db__tph-bar" />
                <div className={`tw-db__tph-body${ph.status === 'active' ? ' tw-db__tph-body--active' : ''}`}>
                  <div className="tw-db__tph-num">{ph.id}</div>
                  <div className="tw-db__tph-name">{t(`13W_Dashboard.State.${ph.name}`)}</div>
                  {ph.status === 'done' && <div className="tw-db__tph-check">✓ Hoàn thành</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="tw-db__stats">
        <div className="tw-db__stat">
          <div className="tw-db__stat-label">{t('13W_Dashboard.wr_month')}</div>
          <div className="tw-db__stat-val" style={{ color: '#C9A84C' }}>54%</div>
          <div className="tw-db__stat-change tw-db__stat-change--up">↑ 4% {t('13W_Dashboard.vs_last_month')}</div>
        </div>
        <div className="tw-db__stat">
          <div className="tw-db__stat-label">{t('13W_Dashboard.pf')}</div>
          <div className="tw-db__stat-val" style={{ color: '#4CAF80' }}>1.72</div>
          <div className="tw-db__stat-change tw-db__stat-change--up">↑ {t('13W_Dashboard.target')}: ≥1.5</div>
        </div>
        <div className="tw-db__stat">
          <div className="tw-db__stat-label">{t('13W_Dashboard.drawdown_month')}</div>
          <div className="tw-db__stat-val" style={{ color: '#E07048' }}>4.2%</div>
          <div className="tw-db__stat-change">{t('13W_Dashboard.limit')}: 6%</div>
        </div>
        <div className="tw-db__stat">
          <div className="tw-db__stat-label">{t('13W_Dashboard.expectancy')}</div>
          <div className="tw-db__stat-val" style={{ color: '#C9A84C' }}>0.38R</div>
          <div className="tw-db__stat-change">{t('13W_Dashboard.target')}: ≥0.3R ✓</div>
        </div>
      </div>

      <div className="tw-db__two-col">
        <div className="tw-db__card">
          <div className="tw-db__card-hd">
            <span className="tw-db__card-title">R tích lũy theo tuần</span>
            <a href="/academy?tab=roadmap" className="tw-db__card-action">{t('13W_Dashboard.details')} →</a>
          </div>
          <div className="tw-db__chart">
            {weeklyR.map((w) => (
              <div
                key={w.week}
                className="tw-db__bar"
                style={{
                  height: `${(Math.abs(w.r) / maxR) * 100}%`,
                  background: w.positive ? '#4CAF80' : '#EF5350',
                }}
                title={`${w.week}: ${w.r > 0 ? '+' : ''}${w.r}R`}
              />
            ))}
          </div>
          <div className="tw-db__chart-labels">
            {weeklyR.map((w) => (
              <span key={w.week} className="tw-db__chart-label">{w.week}</span>
            ))}
          </div>
        </div>

        <div className="tw-db__card">
          <div className="tw-db__card-hd">
            <span className="tw-db__card-title">{t('13W_Dashboard.recent_activity')}</span>
            <span className="tw-db__card-action">{t('13W_Dashboard.details')} →</span>
          </div>
          {activities.map((a, i) => (
            <div key={i} className="tw-db__activity">
              <div className="tw-db__activity-dot" style={{ background: a.color }} />
              <div className="tw-db__activity-text">{a.text}</div>
              <div className="tw-db__activity-time">{a.time}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="tw-db__three-col">
        <div className="tw-db__card">
          <div className="tw-db__card-hd">
            <span className="tw-db__card-title">{t('13W_Dashboard.kpi_this_month')}</span>
          </div>
          {monthlyKpi.map((k) => (
            <div key={k.label} className="tw-db__kpi-row">
              <span className="tw-db__kpi-label">{k.label}</span>
              <span className="tw-db__kpi-val" style={{ color: k.color }}>{k.val}</span>
            </div>
          ))}
        </div>

        <div className="tw-db__card">
          <div className="tw-db__card-hd">
            <span className="tw-db__card-title">{t('13W_Dashboard.checklist_today')}</span>
          </div>
          <div className="tw-db__summary">
            <div className="tw-db__summary-big" style={{ color: '#C9A84C' }}>68%</div>
            <div className="tw-db__summary-sub">17 / 25 {t('13W_Dashboard.actions')}</div>
            <div className="tw-db__mini-track">
              <div className="tw-db__mini-fill" style={{ width: '68%' }} />
            </div>
          </div>
          <a href="/academy?tab=checklist" className="tw-db__ghost-btn">{t('13W_Dashboard.open_checklist')} →</a>
        </div>

        <div className="tw-db__card">
          <div className="tw-db__card-hd">
            <span className="tw-db__card-title">{t('13W_Dashboard.elite_score')}</span>
          </div>
          <div className="tw-db__summary">
            <div className="tw-db__summary-big">4<span className="tw-db__summary-denom">/10</span></div>
            <div className="tw-db__summary-sub">{t('13W_Dashboard.elite_score_step.2')}</div>
            <div className="tw-db__mini-track">
              <div className="tw-db__mini-fill" style={{ width: '40%' }} />
            </div>
          </div>
          <a href="/academy?tab=eval" className="tw-db__ghost-btn">Đánh giá ngay →</a>
        </div>
      </div>
    </div>
  );
}

// Roadmap
const PHASE_STATUS_LABEL: Record<string, string> = {
  done: '✓ Hoàn thành',
  active: '● Đang tiến hành',
  in_progress: '● Đang tiến hành',
  locked: '◌ Chưa mở khóa',
};

const PHASE_STATUS_CSS: Record<string, string> = {
  done: 'done',
  active: 'active',
  in_progress: 'active',
  locked: 'locked',
};

function ScreenRoadmap() {
  const [openPhase, setOpenPhase] = useState<number | null>(0);
  const [activeMonth, setActiveMonth] = useState<Record<number, number>>({});
  const [apiPhases, setApiPhases] = useState<{ name: string; sub: string; status: string; months: { index: number; title: string; status: string; is_locked: boolean }[] }[]>([]);

  useEffect(() => {
    import('services/thirteenWeeks').then(({ fetchRoadmap }) => {
      fetchRoadmap().then((res) => {
        if (!res) return;
        setApiPhases(res.data.map((stage) => ({
          name: stage.stage_name,
          sub: stage.stage_english_name,
          status: stage.months.some(m => m.status === 'in_progress') ? 'in_progress'
            : stage.months.every(m => m.status === 'completed') ? 'done'
              : 'locked',
          months: stage.months.map(m => ({
            index: m.month_index,
            title: m.title,
            status: m.status,
            is_locked: m.is_locked,
          })),
        })));
      });
    });
  }, []);

  const mergedPhases = phases.map((ph, pi) => {
    const api = apiPhases[pi];
    return {
      ...ph,
      name: api?.name ?? ph.name,
      sub: api?.sub ?? ph.sub,
      status: (api?.status ?? ph.status) as string,
      months: ph.months.map((m, mi) => {
        const apiMonth = api?.months[mi];
        return {
          ...m,
          title: apiMonth?.title ?? m.title,
          is_locked: apiMonth?.is_locked ?? false,
          status: apiMonth?.status ?? 'locked',
        };
      }),
    };
  });

  return (
    <div>
      {mergedPhases.map((ph, pi) => {
        const isOpen = openPhase === pi;
        const curMonth = activeMonth[pi] ?? 0;
        const statusCss = PHASE_STATUS_CSS[ph.status] ?? 'locked';
        return (
          <div key={pi} className={`thirteen-weeks__roadmap-phase${isOpen ? ' thirteen-weeks__roadmap-phase--open' : ''}`}>
            <div
              className="thirteen-weeks__phase-header"
              style={{ cursor: ph.status === 'locked' ? 'not-allowed' : 'pointer' }}
              onClick={() => {
                if (ph.status === 'locked') { message.warning('Nội dung giai đoạn này chưa được mở khoá'); return; }
                setOpenPhase(isOpen ? null : pi);
              }}
            >
              <div className={`thirteen-weeks__phase-badge ${ph.colorCls}`}>{pi + 1}</div>
              <div className="thirteen-weeks__phase-meta">
                <div className="thirteen-weeks__phase-title">
                  {ph.name} <span>{ph.sub}</span>
                </div>
                <div className="thirteen-weeks__phase-goal">{ph.goal}</div>
              </div>
              <span className="thirteen-weeks__phase-tag">{ph.num}</span>
              <span className={`thirteen-weeks__phase-status thirteen-weeks__phase-status--${statusCss}`}>
                {PHASE_STATUS_LABEL[ph.status] ?? '◌ Chưa mở khóa'}
              </span>
              <svg className="thirteen-weeks__phase-arrow" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="m3 2 4 4-4 4" />
              </svg>
            </div>
            <div className="thirteen-weeks__phase-body">
              <div className="thirteen-weeks__month-tabs">
                {ph.months.map((m, mi) => (
                  <button
                    key={mi}
                    className={`thirteen-weeks__month-tab${curMonth === mi ? ' thirteen-weeks__month-tab--active' : ''}${m.is_locked ? ' thirteen-weeks__month-tab--locked' : ''}`}
                    onClick={() => {
                      if (m.is_locked) { message.warning('Nội dung tháng này chưa được mở khoá'); return; }
                      setActiveMonth((prev) => ({ ...prev, [pi]: mi }));
                    }}
                  >
                    {m.name}: {m.title}
                    {m.is_locked && <Lock size={12} style={{ display: 'inline', marginLeft: 4, verticalAlign: 'middle' }} />}
                  </button>
                ))}
              </div>
              {ph.months.map((m, mi) => (
                <div key={mi} className={`thirteen-weeks__month-content${curMonth === mi ? ' thirteen-weeks__month-content--active' : ''}`}>
                  <div>
                    <div className="thirteen-weeks__month-section-label">Kỹ năng trọng tâm</div>
                    <div className="thirteen-weeks__skill-tags">
                      {m.skills.map((s) => <span key={s} className="thirteen-weeks__skill-tag">{s}</span>)}
                    </div>
                    <div className="thirteen-weeks__month-section-label">KPI đo lường</div>
                    {m.kpis.map(([label, val]) => (
                      <div key={label} className="thirteen-weeks__kpi-row">
                        <span className="thirteen-weeks__kpi-label">{label}</span>
                        <span className="thirteen-weeks__kpi-val">{val}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="thirteen-weeks__month-section-label">Cảnh báo & rủi ro</div>
                    <div className="thirteen-weeks__warn-box thirteen-weeks__warn-box--amber">
                      <ul>{m.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                    </div>
                    <div className="thirteen-weeks__month-section-label">Trigger phải dừng / review</div>
                    <div className="thirteen-weeks__warn-box thirteen-weeks__warn-box--red">
                      <ul>{m.triggers.map((tr, i) => <li key={i}>{tr}</li>)}</ul>
                    </div>
                    <div className="thirteen-weeks__quote">"{m.quote}"</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// KPI
function KpiTable({ heading, rows }: { heading: string; rows: [string, string, string, string][] }) {
  return (
    <div className="thirteen-weeks__kpi-table-wrap">
      <table className="thirteen-weeks__kpi-table">
        <thead><tr><th>{heading}</th><th>Mục tiêu</th><th>Nhóm</th></tr></thead>
        <tbody>
          {rows.map(([l, v, c, g]) => (
            <tr key={l}>
              <td>{l}</td>
              <td>{v}</td>
              <td><span className={`thirteen-weeks__badge thirteen-weeks__badge--${c}`}>{g}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ScreenKpi() {
  return (
    <div>
      <div className="thirteen-weeks__kpi-big-grid">
        {kpiBigCards.map((c) => (
          <div key={c.label} className="thirteen-weeks__kpi-big-card">
            <div className="thirteen-weeks__kpi-big-card-label">{c.label}</div>
            <div className="thirteen-weeks__kpi-big-card-val">{c.val}</div>
            <div className="thirteen-weeks__kpi-big-card-target">{c.target}</div>
          </div>
        ))}
      </div>
      <div className="thirteen-weeks__kpi-tables">
        <div>
          <KpiTable heading="Chỉ số hiệu suất" rows={kpiPerformanceRows} />
          <KpiTable heading="Tính nhất quán" rows={kpiConsistencyRows} />
        </div>
        <div>
          <KpiTable heading="Quản trị rủi ro" rows={kpiRiskRows} />
          <KpiTable heading="Kỷ luật tâm lý" rows={kpiDisciplineRows} />
        </div>
      </div>
    </div>
  );
}

// Checklist
const TodayPanel = React.lazy(() => import('./TodayPanel'));

const STATUS_COLOR: Record<DayStatus, string> = {
  done: '#4CAF80',
  partial: '#C9A84C',
  missed: '#EF5350',
  today: '#5B9BD5',
  future: 'transparent',
};

const STATUS_LABEL: Record<DayStatus, string> = {
  done: 'Đầy đủ',
  partial: 'Thiếu nhật ký',
  missed: 'Bỏ lỡ',
  today: 'Hôm nay',
  future: '',
};

function ScreenChecklist() {
  const navigate = useNavigate();
  const location = useLocation();
  const [current, setCurrent] = useState(() => dayjs());
  const { loading: calLoading, getDayInfo } = useMonthChecklist(current.year(), current.month() + 1);

  const [selectedDay, setSelectedDay] = useState(() => dayjs().format('YYYY-MM-DD'));

  const params = new URLSearchParams(location.search);
  const detailDate = params.get('date');
  const rawStep = parseInt(params.get('step') ?? '0', 10);
  const detailStep = (rawStep >= 0 && rawStep <= 4 ? rawStep : 0) as StepKey;
  const accountId = params.get('account');

  const goToDetail = (date: string) => navigate(`/academy?tab=checklist&date=${date}&step=0${accountId ? `&account=${accountId}` : ''}`);
  // history.back() only when the calendar is the previous entry; a reloaded or shared detail link
  // has no in-app history (key 'default') and would otherwise leave the site
  const goBack = () => {
    if (location.key !== 'default') {
      window.history.back();
    } else {
      navigate('/academy?tab=checklist', { replace: true });
    }
  };
  const goToStep = (date: string, step: StepKey) => navigate(`/academy?tab=checklist&date=${date}&step=${step}${accountId ? `&account=${accountId}` : ''}`, { replace: true });
  const goToAccount = (id: string) => {
    if (!id || id === accountId) {
      return;
    }
    const newParams = new URLSearchParams(location.search);
    newParams.set('account', id);
    navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
  };

  const selectMonth = (next: Dayjs) => {
    setCurrent(next);
    const isThisMonth = next.isSame(dayjs(), 'month');
    setSelectedDay(isThisMonth ? dayjs().format('YYYY-MM-DD') : next.startOf('month').format('YYYY-MM-DD'));
  };

  if (detailDate) {
    return <DayDetail
      date={detailDate}
      step={detailStep}
      onBack={goBack}
      onStepChange={(step) => goToStep(detailDate, step)}
      accountId={accountId}
      onAccountChange={goToAccount}
    />;
  }

  return (
    <div className="tw-checklist-layout">
      <div className="tw-checklist-layout__cal">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', fontSize: 12 }}>
            {(['done', 'partial', 'missed', 'today'] as DayStatus[]).map(s => (
              <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: STATUS_COLOR[s], display: 'inline-block' }} />
                {STATUS_LABEL[s]}
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => selectMonth(current.subtract(1, 'month'))}
              disabled={current.isSame(dayjs().subtract(1, 'month'), 'month')}
              style={{ background: 'none', border: 'none', fontSize: 18, lineHeight: 1, color: current.isSame(dayjs().subtract(1, 'month'), 'month') ? '#4b5563' : '#C9A84C', cursor: current.isSame(dayjs().subtract(1, 'month'), 'month') ? 'not-allowed' : 'pointer' }}
            >‹</button>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{current.format('MM/YYYY')}</span>
            <button
              onClick={() => selectMonth(current.add(1, 'month'))}
              disabled={current.isSame(dayjs(), 'month')}
              style={{ background: 'none', border: 'none', fontSize: 18, lineHeight: 1, color: current.isSame(dayjs(), 'month') ? '#4b5563' : '#C9A84C', cursor: current.isSame(dayjs(), 'month') ? 'not-allowed' : 'pointer' }}
            >›</button>
          </div>
        </div>
        {calLoading ? <CalendarSkeleton /> : (
          <CalendarGrid
            current={current}
            getDayInfo={getDayInfo}
            selectedDate={selectedDay}
            onSelectDate={(date) => {
              setSelectedDay(date);
              // Future days have nothing to fill in yet: only preview them in the side panel
              if (date <= dayjs().format('YYYY-MM-DD')) goToDetail(date);
            }}
          />
        )}
      </div>
      <aside className="tw-checklist-layout__today">
        <React.Suspense fallback={<div className="tw-today tw-today--loading">Đang tải...</div>}>
          <TodayPanel
            date={selectedDay}
            onOpen={goToDetail}
            dayInfo={getDayInfo(selectedDay)}
          />
        </React.Suspense>
      </aside>
    </div>
  );
}

// Self-eval
function evalStatusText(score: number, total: number): string {
  if (score === 0) return '— Chưa đánh giá —';
  if (score <= 3) return 'Đang xây dựng nền tảng';
  if (score <= 6) return 'Đang tiến bộ';
  if (score <= total - 2) return 'Gần đạt Elite';
  return '★ Elite Trader';
}

function ScreenEval() {
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const score = Object.values(checked).filter(Boolean).length;
  const pct = Math.round((score / evalItems.length) * 100);

  return (
    <div>
      <div className="thirteen-weeks__eval-score-card">
        <div className="thirteen-weeks__eval-score-num">
          {score}<span>/{evalItems.length}</span>
        </div>
        <div className="thirteen-weeks__eval-score-label">Đánh dấu các tiêu chí bạn đã đạt</div>
        <div className="thirteen-weeks__eval-score-bar">
          <div className="thirteen-weeks__eval-score-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="thirteen-weeks__eval-score-status">{evalStatusText(score, evalItems.length)}</div>
      </div>
      <div className="thirteen-weeks__eval-grid">
        {evalItems.map((item, i) => {
          const isChecked = !!checked[i];
          return (
            <div
              key={i}
              className={`thirteen-weeks__eval-card${isChecked ? ' thirteen-weeks__eval-card--checked' : ''}`}
              onClick={() => setChecked((prev) => ({ ...prev, [i]: !prev[i] }))}
            >
              <div className="thirteen-weeks__eval-num-circle">{i + 1}</div>
              <div className="thirteen-weeks__eval-card-text">
                <div className="thirteen-weeks__eval-card-title">{item.title}</div>
                <div className="thirteen-weeks__eval-card-sub">{item.sub}</div>
              </div>
              <div className="thirteen-weeks__eval-toggle" />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Elite+
function ScreenElitePlus() {
  const half = Math.ceil(elitePlusStages.length / 2);
  const cols = [elitePlusStages.slice(0, half), elitePlusStages.slice(half)];

  return (
    <div>
      <div className="thirteen-weeks__eliteplus-hero">
        <h2>BeQuantum: Ascend Beyond Elite</h2>
        <p>
          Sau tháng 12, bạn bước sang giai đoạn tiếp theo — không còn là người lướt sóng,
          mà định hình vị trí và di sản giao dịch của chính mình.
        </p>
      </div>
      <div className="thirteen-weeks__eliteplus-grid">
        {cols.map((col, ci) => (
          <div key={ci} className="thirteen-weeks__card">
            <div className="thirteen-weeks__card-title">
              {ci === 0 ? 'Roadmap Elite+ (Tháng 1–6)' : 'Tiếp tục (Tháng 7–12)'}
            </div>
            {col.map((stage, si) => (
              <div key={si} className="thirteen-weeks__vision-stage">
                <div className="thirteen-weeks__vision-line">
                  <div className={`thirteen-weeks__vision-dot ${stage.colorCls}`}>
                    {stage.period.replace('Tháng ', '')}
                  </div>
                  {si < col.length - 1 && <div className="thirteen-weeks__vision-connector" />}
                </div>
                <div className="thirteen-weeks__vision-content">
                  <div className="thirteen-weeks__vision-title">{stage.title}</div>
                  <div className="thirteen-weeks__vision-period">{stage.period.toUpperCase()}</div>
                  {stage.items.map((item, ii) => (
                    <div key={ii} className="thirteen-weeks__vision-item">{item}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// Screen map
const SCREENS: Record<TabKey, React.ReactNode> = {
  overview: <ScreenOverview />,
  dashboard: <ScreenDashboard />,
  roadmap: <ScreenRoadmap />,
  kpi: <ScreenKpi />,
  checklist: <ScreenChecklist />,
  eval: <ScreenEval />,
  eliteplus: <ScreenElitePlus />,
  handbook: <ScreenHandbook />,
};

const ThirteenWeeksContent: React.FC = () => {
  useTranslation();
  const location = useLocation();

  const rawTab = new URLSearchParams(location.search).get('tab');
  const activeTab: TabKey = isValidTab(rawTab) ? rawTab : 'overview';
  const noScroll = activeTab === 'overview';

  React.useEffect(() => {
    if (!noScroll) return;
    const mq = window.matchMedia('(min-width: 901px)');
    const apply = () => { document.body.style.overflow = mq.matches ? 'hidden' : ''; };
    apply();
    mq.addEventListener('change', apply);
    return () => {
      mq.removeEventListener('change', apply);
      document.body.style.overflow = '';
    };
  }, [noScroll]);

  return (
    <div className={`thirteen-weeks__content${noScroll ? ' thirteen-weeks__content--noscroll' : ''}${activeTab === 'handbook' ? ' thirteen-weeks__content--handbook' : ''}`}>
      {SCREENS[activeTab]}
    </div>
  );
};

export default ThirteenWeeksContent;
